import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useCallback, useRef } from 'react';

import IntegrationService from '../services/api/integrationService';
import logger from '../utils/logger.js';

// Custom hook for API integration
export const useIntegration = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus] = useState({ pending: 0, syncing: false });

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check API health periodically - treat any 2xx as healthy
  useEffect(() => {
    const checkHealth = async () => {
      const health = await IntegrationService.checkApiHealth();
      if (!health.healthy && isOnline) {
        logger.error('API health check failed', { status: health.status, error: health.error });
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 60000); // Every minute

    return () => clearInterval(interval);
  }, [isOnline]);

  return {
    isOnline,
    syncStatus,
    service: IntegrationService,
  };
};

// Hook for dashboard data
export const useDashboardData = (role, options = {}) => {
  const { service } = useIntegration();

  const queryResult = useQuery({
    queryKey: ['dashboard', role, options.timeRange, options.projectId],
    queryFn: () => service.getDashboardData(role, options.timeRange, options.projectId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: options.refetchInterval || 60000, // 1 minute default
    ...options,
  });

  return queryResult;
};

// Hook for projects
export const useProjects = (filters = {}, options = {}) => {
  const { service } = useIntegration();

  const queryResult = useQuery({
    queryKey: ['projects', filters],
    queryFn: () => service.getProjects(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });

  return queryResult;
};

export const useProject = (projectId, options = {}) => {
  const { service } = useIntegration();

  return useQuery({
    queryKey: ['project', projectId],
    queryFn: () => service.getProject(projectId),
    enabled: !!projectId,
    ...options,
  });
};

export const useCreateProject = (options = {}) => {
  const { service } = useIntegration();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectData) => service.createProject(projectData),
    onSuccess: () => {
      queryClient.invalidateQueries(['projects']);
    },
    ...options,
  });
};

// Hook for safety data
export const useIncidents = (filters = {}, options = {}) => {
  const { service } = useIntegration();

  const queryResult = useQuery({
    queryKey: ['incidents', filters],
    queryFn: () => service.getIncidents(filters),
    ...options,
  });

  return queryResult;
};

export const useReportIncident = (options = {}) => {
  const { service } = useIntegration();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (incidentData) => service.reportIncident(incidentData),
    onSuccess: () => {
      queryClient.invalidateQueries(['incidents']);
      queryClient.invalidateQueries(['dashboard']);
    },
    ...options,
  });
};

export const useHazards = (filters = {}, options = {}) => {
  const { service } = useIntegration();

  const queryResult = useQuery({
    queryKey: ['hazards', filters],
    queryFn: () => service.getHazards(filters),
    ...options,
  });

  return queryResult;
};

export const useReports = (filters = {}, options = {}) => {
  const { service } = useIntegration();

  const queryResult = useQuery({
    queryKey: ['reports', filters],
    queryFn: () => service.getReports(filters),
    ...options,
  });

  return queryResult;
};

export const useEvidence = (filters = {}, options = {}) => {
  const { service } = useIntegration();

  const queryResult = useQuery({
    queryKey: ['evidence', filters],
    queryFn: () => service.getFiles(filters), // Assuming evidence is fetched via getFiles
    ...options,
  });

  return queryResult;
};

export const useSchedule = (filters = {}, options = {}) => {
  const { service } = useIntegration();
  const queryClient = useQueryClient();
  const queryKey = ['schedule', filters];

  const queryFn = () => {
    if (typeof service.getSchedule !== 'function') {
      const error = new Error('[useSchedule] Schedule API not wired');
      logger.warn(error.message);
      return Promise.reject(error);
    }
    return service.getSchedule(filters);
  };

  const queryResult = useQuery({
    queryKey,
    queryFn,
    ...options,
  });

  const addEventMutation = useMutation({
    mutationFn: async (newEvent) => {
      // Optimistically update the cache.
      await queryClient.cancelQueries({ queryKey });
      const previousSchedule = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old) => [...(old || []), newEvent]);

      if (typeof service.createScheduleEvent !== 'function') {
        const error = new Error('[useSchedule] createScheduleEvent not wired');
        logger.warn(error.message);
        throw error;
      }

      await service.createScheduleEvent(newEvent);

      return { previousSchedule };
    },
    onError: (err, newEvent, context) => {
      // If the mutation fails, roll back to the previous state.
      queryClient.setQueryData(queryKey, context.previousSchedule);
    },
    onSettled: () => {
      // Invalidate the query to ensure data consistency.
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const addEvent = (eventData) => {
    const newEvent = {
      id: `evt_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      ...eventData,
    };
    addEventMutation.mutate(newEvent);
  };

  const updateEventMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey });
      const previousSchedule = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old) =>
        (old || []).map((event) => (event.id === id ? { ...event, ...data } : event))
      );

      if (typeof service.updateScheduleEvent !== 'function') {
        const error = new Error('[useSchedule] updateScheduleEvent not wired');
        logger.warn(error.message);
        throw error;
      }

      await service.updateScheduleEvent(id, data);

      return { previousSchedule };
    },
    onError: (err, { id, data }, context) => {
      queryClient.setQueryData(queryKey, context.previousSchedule);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateEvent = (id, data) => {
    updateEventMutation.mutate({ id, data });
  };

  const deleteEventMutation = useMutation({
    mutationFn: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previousSchedule = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old) => (old || []).filter((event) => event.id !== id));

      if (typeof service.deleteScheduleEvent !== 'function') {
        const error = new Error('[useSchedule] deleteScheduleEvent not wired');
        logger.warn(error.message);
        throw error;
      }

      await service.deleteScheduleEvent(id);

      return { previousSchedule };
    },
    onError: (err, id, context) => {
      queryClient.setQueryData(queryKey, context.previousSchedule);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteEvent = (id) => {
    deleteEventMutation.mutate(id);
  };

  return { ...queryResult, addEvent, updateEvent, deleteEvent };
};

// Hook for workers
export const useWorkers = (filters = {}, options = {}) => {
  const { service } = useIntegration();

  const queryResult = useQuery({
    queryKey: ['workers', filters],
    queryFn: () => service.getWorkers(filters),
    ...options,
  });

  return queryResult;
};

export const useWorker = (workerId, options = {}) => {
  const { service } = useIntegration();

  return useQuery({
    queryKey: ['worker', workerId],
    queryFn: () => service.getWorker(workerId),
    enabled: !!workerId,
    ...options,
  });
};

// Hook for compliance
export const useComplianceRequirements = (projectId = null, options = {}) => {
  const { service } = useIntegration();

  const queryResult = useQuery({
    queryKey: ['compliance', 'requirements', projectId],
    queryFn: () => service.getComplianceRequirements(projectId),
    ...options,
  });

  return queryResult;
};

export const useComplianceAudits = (filters = {}, options = {}) => {
  const { service } = useIntegration();

  return useQuery({
    queryKey: ['compliance', 'audits', filters],
    queryFn: () => service.getComplianceAudits(filters),
    ...options,
  });
};

// Hook for analytics
export const useSafetyAnalytics = (timeRange = '30d', projectId = null, options = {}) => {
  const { service } = useIntegration();

  const queryResult = useQuery({
    queryKey: ['analytics', 'safety', timeRange, projectId],
    queryFn: () => service.getSafetyAnalytics(timeRange, projectId),
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });

  return queryResult;
};

// Hook for real-time notifications
export const useNotifications = (onNotification, options = {}) => {
  const { service, isOnline } = useIntegration();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);

  const connect = useCallback(() => {
    if (!isOnline) return;

    try {
      wsRef.current = service.subscribeToNotifications((notification) => {
        onNotification(notification);
      });

      wsRef.current.onclose = () => {
        // Attempt to reconnect with exponential backoff
        if (reconnectAttempts.current < 5) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        }
      };

      wsRef.current.onopen = () => {
        reconnectAttempts.current = 0;
      };
    } catch (error) {
      logger.error('Failed to connect to notifications:');
    }
  }, [service, onNotification, isOnline]);

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  return {
    connected: wsRef.current?.readyState === WebSocket.OPEN,
  };
};

// Hook for file uploads
export const useFileUpload = (options = {}) => {
  const { service } = useIntegration();
  const [uploadProgress, setUploadProgress] = useState({});

  const upload = useCallback(
    async (file, metadata = {}) => {
      const uploadId = Date.now().toString();

      try {
        setUploadProgress((prev) => ({
          ...prev,
          [uploadId]: { progress: 0, status: 'uploading' },
        }));

        const result = await service.uploadFile(file, metadata, (progress) => {
          setUploadProgress((prev) => ({
            ...prev,
            [uploadId]: { progress, status: 'uploading' },
          }));
        });

        setUploadProgress((prev) => ({
          ...prev,
          [uploadId]: { progress: 100, status: 'completed', result },
        }));

        if (options.onSuccess) {
          options.onSuccess(result);
        }

        return result;
      } catch (error) {
        setUploadProgress((prev) => ({
          ...prev,
          [uploadId]: { progress: 0, status: 'failed', error },
        }));

        if (options.onError) {
          options.onError(error);
        }

        throw error;
      }
    },
    [service, options]
  );

  return {
    upload,
    uploadProgress,
  };
};

// Hook for batch operations
export const useBatchUpdate = () => {
  const { service } = useIntegration();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (resources) => service.batchUpdate(resources),
    onSuccess: (data, variables) => {
      // Invalidate queries for all updated resource types
      const resourceTypes = [...new Set(variables.map((r) => r.type))];
      resourceTypes.forEach((type) => {
        queryClient.invalidateQueries([type]);
      });
    },
  });
};

// Hook for offline sync
export const useOfflineSync = () => {
  const { service, isOnline } = useIntegration();
  const [syncStatus, setSyncStatus] = useState({ pending: 0, syncing: false });

  const sync = useCallback(async () => {
    if (!isOnline) return;

    setSyncStatus({ pending: 0, syncing: true });

    try {
      await service.retryFailedRequests();
      setSyncStatus({ pending: 0, syncing: false });
    } catch (error) {
      logger.error('Sync failed:');
      setSyncStatus((prev) => ({ ...prev, syncing: false }));
    }
  }, [service, isOnline]);

  useEffect(() => {
    if (isOnline) {
      sync();
    }
  }, [isOnline, sync]);

  return {
    syncStatus,
    sync,
  };
};
